import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import geobuf from 'geobuf';
import Pbf from 'pbf';
import { Layer, Source } from 'react-map-gl/mapbox';
import { useMediaQuery } from 'react-responsive';
import { preferredWaterMagnitudeUnit } from '../../../utils';
import { brandingConfig } from '../../../config';

const s3UrlBase = 'https://seagull-visualization-layers.s3.us-east-2.amazonaws.com';

const WaterMagnitude = (props) => {
  const {
    UTCPath,
    selectedLake,
    isSharing,
  } = props;

  const isMobile = useMediaQuery({ maxWidth: 767 }) && !isSharing;
  const [isLoading, setIsLoading] = useState(false);
  const [waterLayers, setWaterLayers] = useState([]);

  const fetchOfs = async (ofs) => {
    const ofsUrl = `${s3UrlBase}${UTCPath}/${ofs}-water.pbf`;
    const ofsResponse = await fetch(ofsUrl);
    const ofsData = await ofsResponse.arrayBuffer();
    return geobuf.decode(new Pbf(ofsData));
  };

  const fetchData = async (selectedLake) => {
    if (selectedLake.length > 0) {
      const ofs = `l${selectedLake[0][0].toLowerCase()}ofs`;
      const ofsData = await fetchOfs(ofs);
      setWaterLayers([
        {
          lake: 'primaryLake',
          sourceId: 'primaryLakeSource',
          data: ofsData,
        },
      ]);
    } else if (selectedLake.length === 0) {
      const [leofsData, lhofsData, lmofsData, loofsData, lsofsData] = await Promise.all([
        fetchOfs('leofs'),
        fetchOfs('lhofs'),
        fetchOfs('lmofs'),
        fetchOfs('loofs'),
        fetchOfs('lsofs'),
      ]);
      await setWaterLayers([
        {
          lake: 'primaryLake',
          sourceId: 'primaryLakeSource',
          data: leofsData,
        },
        {
          lake: 'lhofs',
          sourceId: 'lhofsSource',
          data: lhofsData,
        },
        {
          lake: 'lmofs',
          sourceId: 'lmofsSource',
          data: lmofsData,
        },
        {
          lake: 'loofs',
          sourceId: 'loofsSource',
          data: loofsData,
        },
        {
          lake: 'lsofs',
          sourceId: 'lsofsSource',
          data: lsofsData,
        },
      ]);
    } setIsLoading(false);
  };

  useEffect(() => {
    if (!isLoading) {
      setIsLoading(true);
      fetchData(selectedLake)
        .then(() => {
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
        });
    }
  }, [selectedLake, UTCPath]);

  const waterStyle = {
    type: 'fill',
    beforeId: 'waterway-label',
    paint: {
      'fill-color':
        [
          'interpolate',
          [
            'cubic-bezier',
            1,
            1,
            0.4,
            0.7,
          ],
          ['get', 'level'],
          0,
          'rgba(0, 0, 0, 0)',
          0.1,
          'rgba(0, 0, 0, 0)',
          0.2,
          '#1693A5',
          0.3,
          '#00d4b0',
          0.4,
          'hsl(88, 92%, 45%)',
          0.5,
          'hsl(73, 91%, 49%)',
          0.6,
          '#fffe0c',
          0.7,
          '#fbbe1a',
          0.8,
          brandingConfig.colors.accent1,
          0.9,
          '#ff0026',
          1.0,
          '#e81e76',
          1.1,
          '#ac0606',
          1.2,
          '#720202',
          1.3,
          '#4b0000',
        ],
      'fill-opacity': 1,
      'fill-antialias': true,
    },
  };
  const waterRange = isMobile
    ? [
      1.2,
      1.0,
      0.8,
      0.6,
      0.4,
      0.2,
      0,
    ]
    : [
      1.3,
      1.2,
      1.1,
      1.0,
      0.9,
      0.8,
      0.7,
      0.6,
      0.5,
      0.4,
      0.3,
      0.2,
      0.0,
    ];
  return (
    <>
      {isMobile
      && (
        <div className="mobile-temp-legend-container">
          <div className="mobile-water-legend">
            {waterRange.map((waterRange, index) => (
              <div className="mobile-temp-legend-item" key={index}>
                <span className="mobile-temp-legend-item-text">
                  {preferredWaterMagnitudeUnit(waterRange)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {waterLayers.map((waterLayer, index) => (
        <Source
          id={waterLayer.sourceId}
          type="geojson"
          data={waterLayer.data}
          key={index}
        />
      ))}
      {waterLayers.map((tempLayer, index) => (
        <Layer
          id={tempLayer.lake}
          source={tempLayer.sourceId}
          {...waterStyle}
          key={index}
        />
      ))}
    </>
  );
};

WaterMagnitude.propTypes = {
  UTCPath: PropTypes.string,
  selectedLake: PropTypes.string,
  isSharing: PropTypes.bool,
};

WaterMagnitude.defaultProps = {
  UTCPath: '',
  selectedLake: '',
  isSharing: false,
};

export default WaterMagnitude;
